import { MultiSelect } from '@volkswagen-onehub/components-core';
import React from 'react';
import { OfertasContext } from '../../context/ofertas-context';
import { useOfertasFilterContext } from '../../context/ofertas-filter-context';
import { capitalizeSentence } from '../../helpers/ofertas-helper';
import { CarlineWithOfertas, FilterTypes, Oferta } from '../../services/ofertas-service/models/ofertas';

export const MobileSelector = ({
	carlines,
	activeCarlineIndex,
	setSelectedOferta,
	carlineSelectedURLRef,
}: {
	carlines: CarlineWithOfertas[];
	activeCarlineIndex: number;
	setSelectedOferta: (oferta: Oferta) => void;
	carlineSelectedURLRef: React.MutableRefObject<boolean>;
}): React.ReactElement => {
	const { ofertasService } = React.useContext(OfertasContext);
	const {
		handleFilteredCarlines,
		selectedGamaFilter,
		selectedMotorizacionFilter,
		setSelectedGamaFilter,
		setSelectedMotorizacionFilter,
		setActiveCarlineIndex,
		anchorScrollOnFilter,
	} = useOfertasFilterContext();

	const carlinesMap: { [key: string]: React.ReactNode } = Object.fromEntries(
		carlines.map((carline, index) => [String(index), `${carline.nuevo ? 'NUEVO ' : ''}${carline.title.toUpperCase()}`]),
	);

	const handleSelectOnChange = (value: string | string[]) => {
		const carlineValue = Array.isArray(value) ? value[0] : value;

		const foundCarline = Object.entries(carlinesMap).find(([, label]) => String(label) === String(carlineValue))?.[0];

		const selectedIndex = Number(foundCarline);
		const selectedCarline = carlines[selectedIndex];

		if (!selectedCarline) {
			return;
		}

		setActiveCarlineIndex(selectedIndex);

		const ofertas = selectedCarline.ofertas;

		//Esto hace que se añada el coche seleccionado a la url
		carlineSelectedURLRef.current = true;

		if (Array.isArray(ofertas) && ofertas.length > 0) {
			setSelectedOferta(ofertas[0]);
		}
	};

	const createFilterOptions = () => {
		const filters = [];

		const gamaItems: { [key: string]: string } = {};
		if (
			ofertasService.checkFilterExists(FilterTypes.PROFESIONAL) &&
			ofertasService.checkFilterAvailable(FilterTypes.PROFESIONAL, FilterTypes.PROFESIONAL, selectedMotorizacionFilter)
		) {
			gamaItems[FilterTypes.PROFESIONAL] = capitalizeSentence(FilterTypes.PROFESIONAL);
		}
		if (
			ofertasService.checkFilterExists(FilterTypes.LIFESTYLE) &&
			ofertasService.checkFilterAvailable(FilterTypes.LIFESTYLE, FilterTypes.LIFESTYLE, selectedMotorizacionFilter)
		) {
			gamaItems[FilterTypes.LIFESTYLE] = capitalizeSentence(FilterTypes.LIFESTYLE);
		}
		if (Object.keys(gamaItems).length > 1) {
			filters.push({ groupTitle: 'Gama', items: gamaItems });
		}

		const motorizacionItems: { [key: string]: string } = {};

		if (
			ofertasService.checkFilterExists(FilterTypes.ELECTRICOS) &&
			ofertasService.checkFilterAvailable(FilterTypes.ELECTRICOS, selectedGamaFilter, FilterTypes.ELECTRICOS)
		) {
			motorizacionItems[FilterTypes.ELECTRICOS] = FilterTypes.ELECTRICOS;
		}

		if (
			ofertasService.checkFilterExists(FilterTypes.HIBRIDOS_ENCHUFABLES) &&
			ofertasService.checkFilterAvailable(
				FilterTypes.HIBRIDOS_ENCHUFABLES,
				selectedGamaFilter,
				FilterTypes.HIBRIDOS_ENCHUFABLES,
			)
		) {
			motorizacionItems[FilterTypes.HIBRIDOS_ENCHUFABLES] = FilterTypes.HIBRIDOS_ENCHUFABLES;
		}

		if (
			ofertasService.checkFilterExists(FilterTypes.COMBUSTION) &&
			ofertasService.checkFilterAvailable(FilterTypes.COMBUSTION, selectedGamaFilter, FilterTypes.COMBUSTION)
		) {
			motorizacionItems[FilterTypes.COMBUSTION] = FilterTypes.COMBUSTION;
		}

		if (Object.keys(motorizacionItems).length > 1) {
			filters.push({ groupTitle: 'Motorización', items: motorizacionItems });
		}

		return filters;
	};

	const handleFilterChange = (optionKey: string) => {
		const currentFilters = getDefaultFilters();

		anchorScrollOnFilter();
		handleFilteredCarlines(optionKey);

		if (currentFilters.includes(optionKey)) {
			if ([FilterTypes.PROFESIONAL, FilterTypes.LIFESTYLE].includes(optionKey as FilterTypes)) {
				setSelectedGamaFilter(null);
			} else {
				setSelectedMotorizacionFilter(null);
			}
		} else {
			if ([FilterTypes.PROFESIONAL, FilterTypes.LIFESTYLE].includes(optionKey as FilterTypes)) {
				setSelectedGamaFilter(optionKey);
			} else {
				setSelectedMotorizacionFilter(optionKey);
			}
		}
		setActiveCarlineIndex(0);
	};

	const getDefaultFilters = () => {
		const defaults = [];
		if (selectedGamaFilter) defaults.push(selectedGamaFilter);
		if (selectedMotorizacionFilter) defaults.push(selectedMotorizacionFilter);
		return defaults;
	};

	const shouldShowFilters = () => {
		return createFilterOptions().length > 0;
	};

	const dinamicLabel = () => {
		const filterOptions = createFilterOptions();
		if (createFilterOptions().length == 1) {
			return filterOptions[0].groupTitle;
		} else {
			return 'Filtros';
		}
	};

	return (
		<div style={{ display: 'flex', gap: '5px' }}>
			{shouldShowFilters() && (
				<div style={{ flex: '1' }}>
					<MultiSelect
						name="filterSelector"
						label={dinamicLabel()}
						options={createFilterOptions()}
						value={getDefaultFilters()}
						onOptionClick={handleFilterChange}
						onChange={() => {}}
					/>
				</div>
			)}

			<div style={{ flex: '1' }}>
				<MultiSelect
					name="selectedCarline"
					isFloating
					singleOptionMode
					defaultSingleOption={String(carlinesMap[String(activeCarlineIndex)] ?? '')}
					options={carlinesMap}
					onChange={handleSelectOnChange}
				/>
			</div>
		</div>
	);
};
